import React, { useMemo, useRef, useState } from "react";
import { usePages } from "../store/PagesContext";
import { Page } from "../types";
import "../styles/ui.css";
import { parse as parseYaml } from "yaml";

function uid() {
  return Math.random().toString(36).slice(2, 10);
}

/** Minimal YAML printer for our shape */
function toYaml(config: Record<string, any>): string {
  const pad = (n: number) => " ".repeat(n);
  const str = (v: any): string => {
    if (typeof v === "string") {
      // Always quote strings that:
      // - Start with special chars (#, ., /, -)
      // - Contain special YAML chars (:, [, ], {, }, |, >, ', ", \, &, *, !, %, @, `)
      // - Start with numbers
      // - Are YAML reserved words or look like booleans/null
      const needsQuotes = (
        v.match(/^[#.\-/]/) ||                           // starts with #, ., -, or /
        v.includes(":") ||                               // contains colon
        v.includes("[") || v.includes("]") ||            // contains brackets
        v.includes("{") || v.includes("}") ||            // contains braces
        v.includes("|") || v.includes(">") ||            // contains pipe or >
        v.includes("'") || v.includes('"') ||            // contains quotes
        v.includes("\\") || v.includes("&") ||           // contains \ or &
        v.includes("*") || v.includes("!") ||            // contains * or !
        v.includes("%") || v.includes("@") ||            // contains % or @
        v.includes("`") || v.includes(",") ||            // contains ` or ,
        v.match(/^\d/) ||                                // starts with number
        v.match(/^(true|false|null|~)$/i) ||             // YAML boolean/null
        v.trim() !== v                                   // has leading/trailing spaces
      );
      
      if (needsQuotes) {
        return `"${v.replace(/"/g, '\\"')}"`;            // escape internal quotes
      }
      return v;
    }
    return String(v);
  };

  const dump = (obj: any, indent = 0): string => {
    if (Array.isArray(obj)) {
      if (!obj.length) return "[]";
      // For arrays, use proper YAML list format
      return obj
        .map((v) => {
          if (typeof v === "object" && v !== null) {
            return `${pad(indent)}- \n${dump(v, indent + 2)}`;
          } else {
            return `${pad(indent)}- ${str(v)}`;
          }
        })
        .join("\n");
    }
    
    if (obj && typeof obj === "object") {
      return Object.entries(obj)
        .map(([k, v]) => {
          const key = str(k);
          
          if (Array.isArray(v)) {
            if (v.length === 0) {
              return `${pad(indent)}${key}: []`;
            } else if (v.length > 0 && typeof v[0] !== "object") {
              // Simple array - use inline format with proper quoting
              const items = v.map(item => str(item)).join(", ");
              return `${pad(indent)}${key}: [${items}]`;
            } else {
              // Complex array - use block format
              return `${pad(indent)}${key}:\n${dump(v, indent + 2)}`;
            }
          } else if (v && typeof v === "object") {
            // Nested object - use block format
            return `${pad(indent)}${key}:\n${dump(v, indent + 2)}`;
          } else {
            // Simple value
            return `${pad(indent)}${key}: ${str(v)}`;
          }
        })
        .join("\n");
    }
    return str(obj ?? "");
  };
  return dump(config, 0);
}

function buildYamlFromPages(pages: Page[]): string {
  const conf: Record<string, any> = {};
  for (const p of pages) {
    const entry: any = {
      Buttons: p.attrs.Buttons,
      Links: p.attrs.Links,
      Input_fields: p.attrs.Input_fields,
      page_fuzzing: p.page_fuzzing,
      selectors: p.selectors,
      user_cases: p.user_cases,
    };
    if (p.depends_on.length) entry.depends_on = p.depends_on;
    if (p.isLogin && p.login) {
      entry.Error = { Username: p.login.Error.Username, Password: p.login.Error.Password };
      entry.Success = { Username: p.login.Success.Username, Password: p.login.Success.Password };
    }
    conf[p.name] = entry;
  }
  return toYaml(conf);
}

export default function Configurator() {
  const { pages, setPages } = usePages();
  const [activeId, setActiveId] = useState<string>(pages[0]?.id);
  const active = pages.find((p) => p.id === activeId);

  // Validation & stats
  const errors = useMemo(() => {
    const msgs: string[] = [];
    const counts: Record<string, number> = {};
    pages.forEach((p) => (counts[p.name] = (counts[p.name] ?? 0) + 1));
    Object.entries(counts).forEach(([name, c]) => c > 1 && msgs.push(`Duplicate page name: "${name}"`));

    for (const p of pages) {
      if (p.isLogin) {
        if (!p.login) msgs.push(`Login section missing on "${p.name}"`);
        else {
          const { Error, Success } = p.login;
          if (!Error.Username || !Error.Password) msgs.push(`Login Error creds missing on "${p.name}"`);
          if (!Success.Username || !Success.Password) msgs.push(`Login Success creds missing on "${p.name}"`);
        }
      }
      if (p.depends_on.includes(p.name)) msgs.push(`Page "${p.name}" cannot depend on itself`);
    }
    return msgs;
  }, [pages]);

  const stats = useMemo(() => {
    const totalSelectors = pages.reduce((acc, p) => acc + p.selectors.length, 0);
    const totalDeps = pages.reduce((acc, p) => acc + p.depends_on.length, 0);
    const loginPages = pages.filter((p) => p.isLogin).length;
    return { pages: pages.length, selectors: totalSelectors, deps: totalDeps, loginPages };
  }, [pages]);

  const yaml = useMemo(() => buildYamlFromPages(pages), [pages]);

  // Actions
  const importYamlRef = useRef<HTMLInputElement>(null);
  const onYamlFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    try {
      const data = parseYaml(text) as Record<string, any>;
      const next: Page[] = Object.entries(data).map(([name, entry]: any) => ({
        id: uid(),
        name,
        isLogin: !!(entry?.Error && entry?.Success),
        attrs: {
          Buttons: !!entry?.Buttons,
          Links: !!entry?.Links,
          Input_fields: !!entry?.Input_fields,
        },
        page_fuzzing: !!entry?.page_fuzzing,
        selectors: Array.isArray(entry?.selectors) ? entry.selectors : [],
        user_cases: Array.isArray(entry?.user_cases) ? entry.user_cases.map((s: any) => String(s).slice(0, 200)) : [],
        depends_on: Array.isArray(entry?.depends_on) ? entry.depends_on : [],
        login:
          entry?.Error && entry?.Success
            ? {
                Error: { Username: entry.Error?.Username ?? "", Password: entry.Error?.Password ?? "" },
                Success: { Username: entry.Success?.Username ?? "", Password: entry.Success?.Password ?? "" },
              }
            : undefined,
      }));
      setPages(next);
      setActiveId(next[0]?.id);
    } catch (err: any) {
      alert(err?.message || "Invalid YAML");
    } finally {
      e.target.value = "";
    }
  };

  const copyYaml = async () => {
    try {
      await navigator.clipboard.writeText(yaml);
    } catch {
      alert("Copy failed");
    }
  };
  const downloadYaml = () => {
    const blob = new Blob([yaml], { type: "text/yaml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "config.yml";
    a.click();
    URL.revokeObjectURL(url);
  };
  const resetAll = () => {
    if (!window.confirm("Reset configuration?")) return;
    localStorage.removeItem("testagent_pages_v1");
    window.location.reload();
  };

  // Edits
  const update = (id: string, fn: (p: Page) => Page) =>
    setPages((prev) => prev.map((p) => (p.id === id ? fn({ ...p }) : p)));

  const addPage = () => {
    const p: Page = {
      id: uid(),
      name: `Page ${pages.length + 1}`,
      isLogin: false,
      attrs: { Buttons: true, Links: true, Input_fields: true },
      page_fuzzing: false,
      selectors: [],
      user_cases: [],
      depends_on: [],
    };
    setPages((prev) => [...prev, p]);
    setActiveId(p.id);
  };

  const remove = (id: string) => {
    const next = pages.filter((p) => p.id !== id);
    setPages(next);
    if (!next.find((p) => p.id === activeId) && next.length) setActiveId(next[0].id);
  };

  const pageNames = useMemo(() => pages.map((p) => p.name), [pages]);

  return (
    <div className="flex flex-col h-screen">
      {/* Top bar (buttons on the right) */}
      <div className="border-b bg-white">
        <div className="max-w-7xl mx-auto h-14 px-4 flex items-center justify-between">
          {/* Left: title */}
          <div className="flex items-center gap-3">
            <div className="text-lg font-semibold text-gray-800">Configurator</div>
          </div>

          {/* Right: actions */}
          <div className="flex items-center gap-2">
            <input
              ref={importYamlRef}
              type="file"
              accept=".yaml,.yml"
              className="hidden"
              onChange={onYamlFile}
            />
            <button className="btn-neutral" onClick={() => importYamlRef.current?.click()}>
              Import YAML
            </button>
            <button className="btn-neutral" onClick={copyYaml}>
              Copy YAML
            </button>
            <button
              className={`btn-neutral ${errors.length ? "opacity-60 cursor-not-allowed" : ""}`}
              disabled={!!errors.length}
              onClick={downloadYaml}
            >
              Download
            </button>
            <button className="btn-danger" onClick={resetAll}>
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Main */}
      <div className="max-w-7xl mx-auto p-6 grid grid-cols-1 lg:grid-cols-3 gap-10 flex-1 overflow-auto">
        {/* Snapshot & Validation */}
        <aside className="col-span-1 space-y-6">
          <div className="card">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold">Project Snapshot</h3>
              <span className="text-xs text-gray-400">autosaved</span>
            </div>
            <div className="grid grid-cols-4 gap-3 text-center">
              <Stat label="Pages" value={pages.length} />
              <Stat label="Selectors" value={stats.selectors} />
              <Stat label="Depends" value={stats.deps} />
              <Stat label="Login pgs" value={stats.loginPages} />
            </div>
          </div>

          {!!errors.length && (
            <div className="card border-red-200 bg-red-50">
              <div className="font-semibold text-red-700 mb-2">Issues</div>
              <ul className="list-disc ml-5 space-y-1 text-red-700 text-sm">
                {errors.map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="card">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold">Pages</h3>
              <button className="btn-xs" onClick={addPage}>
                + Add
              </button>
            </div>
            <ul className="space-y-1">
              {pages.map((p) => (
                <li
                  key={p.id}
                  className={`flex items-center justify-between rounded px-2 py-1 ${
                    p.id === activeId ? "bg-indigo-50" : "hover:bg-gray-50"
                  }`}
                >
                  <button className="text-left flex-1" onClick={() => setActiveId(p.id)}>
                    {p.name}
                  </button>
                  <button className="text-red-600 text-sm" onClick={() => remove(p.id)}>
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        {/* Editor + YAML */}
        <section className="col-span-2 space-y-6">
          <div className="card">
            {!active ? (
              <div className="text-gray-500">Select or add a page.</div>
            ) : (
              <>
                <div className="grid sm:grid-cols-2 gap-4 mb-6">
                  <label className="flex flex-col gap-1">
                    <span className="text-sm text-gray-600">Page Name</span>
                    <input
                      className="input"
                      value={active.name}
                      onChange={(e) => update(active.id, (p) => ({ ...p, name: e.target.value }))}
                    />
                  </label>

                  <label className="flex items-center gap-2 sm:mt-7">
                    <input
                      type="checkbox"
                      checked={active.isLogin}
                      onChange={(e) =>
                        update(active.id, (p) => ({
                          ...p,
                          isLogin: e.target.checked,
                          login: e.target.checked
                            ? p.login ?? {
                                Error: { Username: "", Password: "" },
                                Success: { Username: "", Password: "" },
                              }
                            : undefined,
                        }))
                      }
                    />
                    <span className="text-sm">Is Login Page</span>
                  </label>
                </div>

                {active.isLogin && active.login && (
                  <div className="grid sm:grid-cols-2 gap-4 mb-8">
                    <fieldset className="fieldset">
                      <legend className="legend">Login Error</legend>
                      <label className="flex flex-col gap-1">
                        <span className="text-xs text-gray-500">Username</span>
                        <input
                          className="input"
                          value={active.login.Error.Username}
                          onChange={(e) =>
                            update(active.id, (p) => ({
                              ...p,
                              login: { ...p.login!, Error: { ...p.login!.Error, Username: e.target.value }, Success: p.login!.Success },
                            }))
                          }
                        />
                      </label>
                      <label className="flex flex-col gap-1 mt-2">
                        <span className="text-xs text-gray-500">Password</span>
                        <input
                          className="input"
                          value={active.login.Error.Password}
                          onChange={(e) =>
                            update(active.id, (p) => ({
                              ...p,
                              login: { ...p.login!, Error: { ...p.login!.Error, Password: e.target.value }, Success: p.login!.Success },
                            }))
                          }
                        />
                      </label>
                    </fieldset>

                    <fieldset className="fieldset">
                      <legend className="legend">Login Success</legend>
                      <label className="flex flex-col gap-1">
                        <span className="text-xs text-gray-500">Username</span>
                        <input
                          className="input"
                          value={active.login.Success.Username}
                          onChange={(e) =>
                            update(active.id, (p) => ({
                              ...p,
                              login: { ...p.login!, Success: { ...p.login!.Success, Username: e.target.value }, Error: p.login!.Error },
                            }))
                          }
                        />
                      </label>
                      <label className="flex flex-col gap-1 mt-2">
                        <span className="text-xs text-gray-500">Password</span>
                        <input
                          className="input"
                          value={active.login.Success.Password}
                          onChange={(e) =>
                            update(active.id, (p) => ({
                              ...p,
                              login: { ...p.login!, Success: { ...p.login!.Success, Password: e.target.value }, Error: p.login!.Error },
                            }))
                          }
                        />
                      </label>
                    </fieldset>
                  </div>
                )}

                <div className="grid lg:grid-cols-3 gap-4 mt-6">
                  <fieldset className="fieldset">
                    <legend className="legend">Attributes</legend>
                    {(["Buttons", "Links", "Input_fields"] as const).map((k) => (
                      <label key={k} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={active.attrs[k]}
                          onChange={(e) => update(active.id, (p) => ({ ...p, attrs: { ...p.attrs, [k]: e.target.checked } }))}
                        />
                        {k.replace("_", " ")}
                      </label>
                    ))}
                    <label className="flex items-center gap-2 text-sm mt-2">
                      <input
                        type="checkbox"
                        checked={active.page_fuzzing}
                        onChange={(e) => update(active.id, (p) => ({ ...p, page_fuzzing: e.target.checked }))}
                      />
                      page_fuzzing
                    </label>
                  </fieldset>

                  <fieldset className="fieldset lg:col-span-2">
                    <legend className="legend">Selectors</legend>
                    <SelectorEditor
                      value={active.selectors}
                      onChange={(selectors) => update(active.id, (p) => ({ ...p, selectors }))}
                    />
                  </fieldset>
                </div>
                
                {/* User case display */}
                <div className="grid lg:grid-cols-3 gap-4 mt-4">
                  <fieldset className="fieldset lg:col-span-3">
                    <legend className="legend">User cases (≤ 200 chars)</legend>
                    <UserCasesEditor
                      value={active.user_cases ?? []}
                      onChange={(user_cases) =>
                        update(active.id, (p) => ({ ...p, user_cases }))
                      }
                    />
                  </fieldset>
                </div>

                <fieldset className="fieldset mt-4">
                  <legend className="legend">Depends on</legend>
                  <div className="flex flex-wrap gap-3">
                    {pageNames
                      .filter((n) => n !== active.name)
                      .map((n) => {
                        const checked = active.depends_on.includes(n);
                        return (
                          <label key={n} className="flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) =>
                                update(active.id, (p) => ({
                                  ...p,
                                  depends_on: e.target.checked
                                    ? [...p.depends_on, n]
                                    : p.depends_on.filter((x) => x !== n),
                                }))
                              }
                            />
                            {n}
                          </label>
                        );
                      })}
                  </div>
                </fieldset>
              </>
            )}
          </div>

          {/* YAML preview */}
          <div className="card">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold">config.yaml (preview)</h3>
              <div className="flex gap-2">
                <button className="btn-neutral" onClick={copyYaml}>Copy</button>
                <button
                  className={`btn-neutral ${errors.length ? "opacity-60 cursor-not-allowed" : ""}`}
                  disabled={!!errors.length}
                  onClick={downloadYaml}
                >
                  Download
                </button>
              </div>
            </div>
            {!!errors.length && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2 mb-3">
                Fix the issues above to enable download.
              </div>
            )}
            <pre className="bg-gray-50 border rounded p-3 text-xs overflow-auto max-h-80 whitespace-pre-wrap">
{yaml}
            </pre>
          </div>
        </section>
      </div>
    </div>
  );
}

/** Simple selectors widget */
function SelectorEditor({
  value,
  onChange,
}: {
  value: string[];
  onChange: (v: string[]) => void;
}) {
  const [txt, setTxt] = useState("");
  const add = () => {
    const v = txt.trim();
    if (!v) return;
    onChange([...value, v]);
    setTxt("");
  };
  const remove = (i: number) => onChange(value.filter((_, idx) => idx !== i));
  return (
    <div>
      <div className="flex gap-2">
        <input
          className="input flex-1"
          placeholder="#selector or .class"
          value={txt}
          onChange={(e) => setTxt(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
        />
        <button className="btn-neutral" onClick={add}>Add</button>
      </div>
      {value.length ? (
        <ul className="flex flex-wrap gap-2 mt-2">
          {value.map((s, i) => (
            <li key={`${s}-${i}`} className="px-2 py-1 bg-gray-100 border rounded text-xs flex items-center gap-2">
              <span>{s}</span>
              <button className="text-red-600" onClick={() => remove(i)}>✕</button>
            </li>
          ))}
        </ul>
      ) : (
        <div className="text-xs text-gray-500 mt-2">No selectors yet.</div>
      )}
    </div>
  );
}

function UserCasesEditor({ value, onChange }: { value: string[]; onChange: (v: string[]) => void; }) {
  const [text, setText] = useState("");

  function addItem() {
    let t = text || "";
    if (t.length > 200) {
      t = t.substring(0, 200);
    }
    if (t.trim().length === 0) {
      return;
    }
    const next: string[] = [];
    for (let i = 0; i < value.length; i++) {
      next[i] = value[i];
    }
    next.push(t);
    onChange(next);
    setText("");
  }

  function removeItem(which: number) {
    const next: string[] = [];
    for (let i = 0; i < value.length; i++) {
      if (i !== which) {
        next.push(value[i]);
      }
    }
    onChange(next);
  }

  return (
    <div>
      <div className="flex gap-2">
        <input
          className="input flex-1"
          placeholder="Describe a required case, i.e., 'Login then navigate to /products'"
          value={text}
          onChange={(e) => {
            const v = e.target.value || "";
            setText(v.length > 200 ? v.substring(0, 200) : v);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              addItem();
            }
          }}
        />
        <button className="btn-neutral" onClick={addItem}>Add</button>
      </div>
      {value.length ? (
        <ul className="flex flex-wrap gap-2 mt-2">
          {value.map((s, i) => (
            <li key={`${s}-${i}`} className="px-2 py-1 bg-gray-100 border rounded text-xs flex items-center gap-2">
              <span>{s}</span>
              <button className="text-red-600" onClick={() => removeItem(i)}>✕</button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg bg-gray-50 border p-3">
      <div className="text-xl font-semibold">{value}</div>
      <div className="text-xs text-gray-500">{label}</div>
    </div>
  );
}
